// Self-hosted GlitchTip (Sentry-API-compatible) browser error tracking.
//
// Guarded init: this is a NO-OP unless a DSN is configured via VITE_SENTRY_DSN
// *and* we are running a production build. That keeps dev and test runs from
// ever phoning home. It is errors-only by design (no tracing, no session
// replay, no PII), so it stays cheap and privacy-preserving. Events go to the
// no-Access GlitchTip ingest host defined in .env.production.
//
// The DSN is a publishable client (browser) key: it is meant to ship in the
// bundle, so committing it to .env.production is safe.
import * as Sentry from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN;

// Crawlers are not members, and their breakage is not our incident. bingbot
// crawling /Login is what raised UI-APP-6 and UI-APP-7: two alert emails for a
// chunk fetch no person ever saw fail.
//
// This regex is DELIBERATELY CASE SENSITIVE, and that is the whole trick. Case
// is what separates a crawler token from an Android device brand: crawlers
// spell it "bot" or "Bot" (bingbot, Googlebot, YandexBot, AhrefsBot, PetalBot)
// while the brands that contain those same three letters are all caps in the
// model field, as in "CUBOT NOTE 20 PRO" and "CUBOT KING KONG 5 PRO". A
// case-insensitive version silently drops a member on a CUBOT handset out of
// error tracking, and nobody would ever find out. The trailing lookahead is
// the smaller guard: it keeps "Abbott/1.0" and "CUBOT_X30" out.
//
// facebookexternalhit is listed by name because it is not a bot-shaped word
// and this app has public SharedAdventCard routes that members post to
// Facebook, so its link scraper does reach us.
//
// sentry.test.ts pins both halves against a corpus of real user agents. Add a
// new token there first, not here.
const CRAWLER_UA =
  /(?:bot|Bot|crawler|Crawler|spider|Spider|Slurp|BingPreview|HeadlessChrome|facebookexternalhit)(?![a-z0-9_])/;

/** Exported for the corpus test; the module-level init below is the only use. */
export const isCrawlerUserAgent = (userAgent: string): boolean =>
  CRAWLER_UA.test(userAgent);

const isCrawler =
  typeof navigator !== "undefined" && isCrawlerUserAgent(navigator.userAgent);

if (dsn && import.meta.env.PROD && !isCrawler) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0,
  });
}

export { Sentry };
